<script lang="ts">
  import { push, link } from "svelte-spa-router";
  import { onMount } from "svelte";
  import EditorForm from "../lib/components/EditorForm.svelte";
  import { entries, bySlug } from "../lib/content";
  import { isSignedIn } from "../lib/adminStore";
  import { emptyEditorState, serialize } from "../lib/serializeFrontmatter";
  import type { EditorState } from "../lib/serializeFrontmatter";
  import { putSop } from "../lib/api";
  import { applyOverride } from "../lib/runtime";

  let state: EditorState = emptyEditorState();
  let saving = false;
  let error: string | null = null;

  onMount(() => {
    if (!$isSignedIn) push("/admin");
  });

  async function save() {
    error = null;
    if (!state.slug) {
      error = "Slug is required.";
      return;
    }
    if (!/^[a-z0-9][a-z0-9-]*$/.test(state.slug)) {
      error = "Slug must be lowercase letters, digits, and hyphens.";
      return;
    }
    if (bySlug($entries, state.slug)) {
      error = `An SOP with slug "${state.slug}" already exists.`;
      return;
    }
    if (!state.title) {
      error = "Title is required.";
      return;
    }
    state.updated = new Date().toISOString().slice(0, 10);
    const md = serialize(state);
    saving = true;
    try {
      await putSop(state.slug, md);
      applyOverride(state.slug, md);
      push(`/sop/${state.slug}`);
    } catch (e) {
      error = (e as Error).message;
    } finally {
      saving = false;
    }
  }

  function cancel() {
    push("/admin");
  }
</script>

{#if !$isSignedIn}
  <div class="panel p-8 text-center">
    <p class="text-text-muted mb-4">Sign in to create SOPs.</p>
    <a use:link href="/admin" class="btn-primary">Go to admin</a>
  </div>
{:else}
  <div class="flex flex-col gap-4">
    <div class="flex items-center gap-2 text-sm text-text-muted">
      <a use:link href="/admin" class="hover:text-text">Admin</a>
      <span>/</span>
      <span>New SOP</span>
    </div>
    <h1 class="text-2xl font-semibold tracking-tight">New SOP</h1>

    <EditorForm
      bind:state
      isNew={true}
      saving={saving}
      error={error}
      onsubmit={save}
      oncancel={cancel}
    />
  </div>
{/if}
